import React, { useState, useEffect } from "react";
import { DatabaseActions } from "./DatabaseActions";
import { partService, Part } from "../Services/PartService";
import countryCodes from "../data/countryCodes.json";
import toast from "react-hot-toast";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { useDeleteConfirmation } from "../hooks/useDeleteConfirmation";

interface LimitInfo {
  canAdd: boolean;
  currentCount: number;
  limit: number | string;
  tier: string;
  wouldExceedBy?: number;
}

interface ServerError {
  message: string;
  limitInfo?: LimitInfo;
}

export const PartsDatabase: React.FC = () => {
  const [parts, setParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState<Part | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortField, setSortField] = useState<keyof Part>("partNumber");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [formData, setFormData] = useState<Partial<Part>>({
    partNumber: "",
    description: "",
    hsCode: "",
    countryOfOrigin: "",
    unitValue: 0,
    notes: "",
    usmca: "",
    usmcaOriginCriteria: "",
    usmcaQualificationMethod: "",
    vendorManufacturer: ""
  });

  const {
    showDeleteModal,
    itemToDelete,
    isDeleting,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    getItemName
  } = useDeleteConfirmation({
    items: parts,
    setItems: setParts,
    deleteFunction: partService.deletePart,
    getItemName: (part: Part) => part.partNumber,
    getItemId: (part: Part) => part._id || '',
    itemType: 'Part'
  });

  const countryOptions = Object.entries(countryCodes).map(([name, code]) => ({
    name,
    code
  }));

  const fetchParts = async () => {
    setLoading(true);
    try {
      const data = await partService.getAllParts();
      setParts(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching parts:", err);
      setError("Failed to load parts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParts();
  }, []);

  // Function to handle sorting
  const handleSort = (field: keyof Part) => {
    if (field === sortField) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it as sort field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Function to get sorted parts
  const getSortedParts = () => {
    // Create a copy of parts to avoid mutating state directly
    const sortedParts = [...parts];

    return sortedParts.sort((a, b) => {
      // Handle undefined values
      const valueA = a[sortField] || "";
      const valueB = b[sortField] || "";

      // For alphanumeric sorting of part numbers
      if (sortField === "partNumber") {
        // Natural sort algorithm for alphanumeric strings
        return sortDirection === "asc"
          ? valueA.toString().localeCompare(valueB.toString(), undefined, { numeric: true, sensitivity: 'base' })
          : valueB.toString().localeCompare(valueA.toString(), undefined, { numeric: true, sensitivity: 'base' });
      }

      // For other fields, use standard string comparison
      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleAddPart = () => {
    setSelectedPart(null);
    setFormData({
      partNumber: "",
      description: "",
      hsCode: "",
      countryOfOrigin: "",
      unitValue: 0,
      notes: ""
    });
    setShowModal(true);
  };

  const handleEditPart = (part: Part) => {
    setSelectedPart(part);
    setFormData({ ...part });
    setShowModal(true);
  };

  const handleDeletePart = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this part?")) return;

    try {
      await partService.deletePart(id);
      setParts(parts.filter(part => part._id !== id));
      toast.success("Part deleted successfully");
    } catch (err: any) {
      console.error("Error deleting part:", err);
      const errorMessage = err.response?.data?.message || "Failed to delete part. Please try again.";
      toast.error(errorMessage);
    }
  };

  // Function to format HS code as user types
  const formatHSCode = (input: string): string => {
    // Remove all non-numeric characters (including periods)
    const digitsOnly = input.replace(/\D/g, '');

    // Limit to 10 digits maximum
    const limitedDigits = digitsOnly.slice(0, 10);

    // Format based on number of digits
    if (limitedDigits.length <= 6) {
      // Format as ####.## for 6 digits or less
      if (limitedDigits.length <= 4) {
        return limitedDigits;
      } else {
        return `${limitedDigits.slice(0, 4)}.${limitedDigits.slice(4)}`;
      }
    } else {
      // Format as ####.##.##.## for 7-10 digits
      return `${limitedDigits.slice(0, 4)}.${limitedDigits.slice(4, 6)}.${limitedDigits.slice(6, 8)}.${limitedDigits.slice(8)}`;
    }
  };

  // Function to count digits in HS code
  const countHSCodeDigits = (hsCode: string): number => {
    return hsCode.replace(/\D/g, '').length;
  };

  // Function to validate HS code format
  const isValidHSCode = (hsCode: string): boolean => {
    if (!hsCode) return false;

    // Count the actual digits (excluding periods)
    const digitCount = hsCode.replace(/\D/g, '').length;

    // Only allow 6 or 10 digits
    if (digitCount !== 6 && digitCount !== 10) {
      return false;
    }

    // Check if it matches the correct format for the digit count
    if (digitCount === 6) {
      return /^\d{4}\.\d{2}$/.test(hsCode);
    } else if (digitCount === 10) {
      return /^\d{4}\.\d{2}\.\d{2}\.\d{2}$/.test(hsCode);
    }

    return false;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'hsCode') {
      // Apply formatting for HS code
      const formattedValue = formatHSCode(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      // Handle other fields normally
      setFormData(prev => ({
        ...prev,
        [name]: name === 'unitValue' || name === 'weight' ? parseFloat(value) || 0 : value
      }));
    }
  };

  const handleHSCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Get pasted content
    const pastedText = e.clipboardData.getData('text');

    // Format the pasted content
    const formattedValue = formatHSCode(pastedText);

    // Prevent default paste behavior
    e.preventDefault();

    // Update the form data with the formatted value
    setFormData(prev => ({
      ...prev,
      hsCode: formattedValue
    }));
  };

  // Function to show subscription limit error with upgrade option
  const showLimitError = (limitInfo: LimitInfo) => {
    const limitText = limitInfo.limit === 'unlimited' ? 'unlimited' : limitInfo.limit.toString();

    toast.error(
      <div className="space-y-2">
        <div className="font-medium">Part Limit Reached</div>
        <div className="text-sm">
          Your <span className="capitalize font-medium">{limitInfo.tier}</span> plan allows {limitText} parts.
          <br />
          You currently have {limitInfo.currentCount} parts.
        </div>
        <button
          onClick={() => {
            // Navigate to upgrade page or show upgrade modal
            window.location.href = '/account#subscription';
          }}
          className="mt-2 px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
        >
          Upgrade Plan
        </button>
      </div>,
      {
        duration: 8000,
        style: {
          maxWidth: '400px',
        }
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Format and validate HS code
    const formattedHSCode = formatHSCode(formData.hsCode || '');

    if (!isValidHSCode(formattedHSCode)) {
      toast.error('HS Code must contain either 6 digits (####.##) or 10 digits (####.##.##.##)');
      setIsSubmitting(false);
      return;
    }

    try {
      const dataToSubmit = {
        ...formData,
        hsCode: formattedHSCode
      };

      if (selectedPart && selectedPart._id) {
        // Update existing part
        const updatedPart = await partService.updatePart(selectedPart._id, dataToSubmit as Part);
        setParts(parts.map(p => p._id === selectedPart._id ? updatedPart : p));
        toast.success("Part updated successfully");
      } else {
        // Create new part
        const newPart = await partService.createPart(dataToSubmit as Part);
        setParts([...parts, newPart]);
        toast.success("Part added successfully");
      }
      setShowModal(false);
    } catch (err: any) {
      console.error("Error saving part:", err);

      // Handle different types of errors
      if (err.response?.status === 403) {
        // Subscription limit error
        const serverError: ServerError = err.response.data;
        if (serverError.limitInfo) {
          showLimitError(serverError.limitInfo);
        } else {
          toast.error(serverError.message || "You've reached your subscription limit");
        }
      } else if (err.response?.status === 400) {
        // Validation error
        const errorMessage = err.response.data.message || "Invalid data provided";
        toast.error(errorMessage);
      } else {
        // Generic error
        toast.error("Failed to save part. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the sorted parts
  const sortedParts = getSortedParts();

  // Helper function to render sort indicator
  const renderSortIndicator = (field: keyof Part) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  return (
    <div className="p-4">
      <h3 className="text-xl font-semibold mb-3">Parts Database</h3>
      <DatabaseActions databaseType="parts" onRefresh={fetchParts} />

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-medium">Parts List</h4>
          <button
            onClick={handleAddPart}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
          >
            Add New Part
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">Loading parts...</div>
        ) : parts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No parts found. Add a new part or upload parts from a file.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("partNumber")}
                  >
                    Part Number {renderSortIndicator("partNumber")}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("description")}
                  >
                    Description {renderSortIndicator("description")}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("hsCode")}
                  >
                    HS Code {renderSortIndicator("hsCode")}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                    onClick={() => handleSort("countryOfOrigin")}
                  >
                    Origin {renderSortIndicator("countryOfOrigin")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider select-none">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedParts.map((part) => (
                  <tr key={part._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {part.partNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {part.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {part.hsCode || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {part.countryOfOrigin || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditPart(part)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(part)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={getItemName}
        itemType="Part"
        isDeleting={isDeleting}
      />

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">
                  {selectedPart ? "Edit Part" : "Add New Part"}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                  disabled={isSubmitting}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="partNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Part Number *
                    </label>
                    <input
                      type="text"
                      id="partNumber"
                      name="partNumber"
                      value={formData.partNumber}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      disabled={isSubmitting}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label htmlFor="hsCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      HS Code * (must be 6 or 10 digits)
                    </label>
                    <input
                      type="text"
                      id="hsCode"
                      name="hsCode"
                      value={formData.hsCode}
                      onChange={handleInputChange}
                      onPaste={handleHSCodePaste}
                      disabled={isSubmitting}
                      className={`mt-1 block w-full border ${formData.hsCode && !isValidHSCode(formData.hsCode)
                        ? 'border-red-500'
                        : 'border-gray-300'
                        } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                      placeholder="e.g., 8471.30"
                      required
                    />
                    {formData.hsCode && !isValidHSCode(formData.hsCode) && (
                      <p className="mt-1 text-sm text-red-500">
                        HS Code must contain either 6 or 10 digits.
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Format: ####.## (6 digits) or ####.##.##.## (10 digits)
                    </p>
                  </div>

                  <div>
                    <label htmlFor="countryOfOrigin" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Country of Origin
                    </label>
                    <select
                      id="countryOfOrigin"
                      name="countryOfOrigin"
                      value={formData.countryOfOrigin}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select a country...</option>
                      {countryOptions.sort((a, b) => a.name.localeCompare(b.name)).map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="unitValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Unit Value
                      </label>
                      <input
                        type="number"
                        id="unitValue"
                        name="unitValue"
                        value={formData.unitValue}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        disabled={isSubmitting}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="vendorManufacturer" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Vendor/Manufacturer
                    </label>
                    <input
                      type="text"
                      id="vendorManufacturer"
                      name="vendorManufacturer"
                      value={formData.vendorManufacturer}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="usmca" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        USMCA
                      </label>
                      <select
                        id="usmca"
                        name="usmca"
                        value={formData.usmca}
                        onChange={handleInputChange}
                        disabled={isSubmitting || !['CA', 'MX', 'US'].includes(formData.countryOfOrigin || '')}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">Select...</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                      {!['CA', 'MX', 'US'].includes(formData.countryOfOrigin || '') && formData.countryOfOrigin && (
                        <p className="mt-1 text-xs text-amber-500">
                          USMCA only applies to goods from Canada, Mexico, or the United States.
                        </p>
                      )}
                    </div>

                    {formData.usmca === "Yes" && (
                      <div>
                        <label htmlFor="usmcaOriginCriteria" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          USMCA Origin Criteria
                        </label>
                        <input
                          type="text"
                          id="usmcaOriginCriteria"
                          name="usmcaOriginCriteria"
                          value={formData.usmcaOriginCriteria}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                    )}
                  </div>

                  {formData.usmca === "Yes" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="usmcaQualificationMethod" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          USMCA Qualification Method
                        </label>
                        <input
                          type="text"
                          id="usmcaQualificationMethod"
                          name="usmcaQualificationMethod"
                          value={formData.usmcaQualificationMethod}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      disabled={isSubmitting}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || (!!formData.hsCode && !isValidHSCode(formData.hsCode))}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSubmitting
                      ? (selectedPart ? "Updating..." : "Adding...")
                      : (selectedPart ? "Update Part" : "Add Part")
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


